/**
 * Greeting Generation Service
 *
 * Low-level API client for AI greeting generation. Assistant UI must call these
 * only via `useAiWritingGeneration` (PBI #1152) so e-card and mailed stay on one
 * orchestration path — enforced by `aiWritingGenerationCallSites.test.ts`.
 *
 * Two transports:
 * - `generateGreeting` — single POST (mailed path + e-card stream fallback)
 * - `streamGreeting` — SSE typing effect (e-card only; server rejects mailed mode)
 *
 * AI settings (per-event default instructions + Tone override + Notes gate) live on
 * `ContactEventType`. `checkInstructionsQuality` is the paid-only classifier for
 * time-bound wording in default instructions.
 */

import apiClient from './apiClient';
import { b2cAuthService } from './b2cAuthService';
import { SUPPRESS_UPGRADE_MODAL_HEADER } from '../constants/apiHeaders';
import type { GenerateGreetingRequest } from '../constants/greetingGenerationModes';

export type { GenerateGreetingRequest };
export { GreetingGenerationModes } from '../constants/greetingGenerationModes';

/**
 * Minimum non-whitespace length before the instructions quality endpoint runs a
 * paid model call. Shorter text returns 200 with no warning server-side; the SPA
 * uses the same threshold to avoid unnecessary traffic.
 *
 * Keep aligned with `InstructionsQualityCheckConstants.MinimumInstructionsLength` in the API.
 */
export const INSTRUCTIONS_QUALITY_MIN_CHARS = 20;

/** Paid-only classifier: time-bound wording in default instructions. */
export interface InstructionsQualityCheckRequest {
  contactId: number;
  eventTypeId: number;
  instructions: string;
}

export interface InstructionsQualityCheckResponse {
  needsWarning: boolean;
  confidence?: number | null;
}

/**
 * Runs the token-minimal Y/N quality check. Sends {@link SUPPRESS_UPGRADE_MODAL_HEADER}
 * so free-plan 402 responses do not open the global UpgradeModal — callers render
 * an inline upsell instead.
 */
export const checkInstructionsQuality = async (
  body: InstructionsQualityCheckRequest,
  options?: { signal?: AbortSignal }
): Promise<InstructionsQualityCheckResponse> => {
  const response = await apiClient.post<InstructionsQualityCheckResponse>(
    '/greeting-generation/instructions-quality-check',
    body,
    {
      signal: options?.signal,
      headers: { [SUPPRESS_UPGRADE_MODAL_HEADER]: 'true' },
    }
  );
  return response.data;
};

/** Response from the non-streaming /generate endpoint. */
export interface GenerateGreetingResponse {
  subjectLine: string;
  greetingBody: string;
  regenerationsRemaining: number;
  /**
   * Non-blocking occasion-hint dictionary matches from the API (same shape as SSE `done`).
   * Omitted or empty when none matched.
   */
  occasionHintWarnings?: string[];
}

/** Body of one SSE event from /generate-stream. */
export type GreetingStreamEvent =
  | { event: 'field-start'; field: 'subjectLine' | 'greetingBody' }
  | { event: 'delta'; field: 'subjectLine' | 'greetingBody'; text: string }
  | { event: 'done'; remaining: number; occasionHintWarnings?: string[] }
  | { event: 'error'; message: string; code: string; failReason?: string };

/**
 * Calls the non-streaming /generate endpoint. Both the AI Writing Assistant
 * fallback and any non-UI caller use this.
 */
export const generateGreeting = async (
  request: GenerateGreetingRequest
): Promise<GenerateGreetingResponse> => {
  const response = await apiClient.post<GenerateGreetingResponse>(
    '/greeting-generation/generate',
    request
  );
  return response.data;
};

/**
 * Streams generation deltas over Server-Sent-Events.
 *
 * Designed to feel like a typing effect in the modal: the server emits
 * `field-start` for each target textarea, a sequence of `delta` events with
 * incremental text, and finally a `done` event with the remaining quota.
 * Validation failures arrive as `error` events before `done`.
 *
 * `signal` should be an AbortSignal wired to the modal's Cancel button so the
 * connection (and the backing OpenAI call) can be aborted cleanly. Caller is
 * responsible for falling back to {@link generateGreeting} if this throws.
 */
export const streamGreeting = async (
  request: GenerateGreetingRequest,
  onEvent: (e: GreetingStreamEvent) => void,
  signal: AbortSignal
): Promise<void> => {
  const baseURL = apiClient.defaults.baseURL ?? '';
  const url = `${baseURL.replace(/\/$/, '')}/greeting-generation/generate-stream`;

  // Must match apiClient's request interceptor: the Bearer token is obtained via
  // MSAL on every axios call — it is NOT stored on apiClient.defaults.headers.common,
  // so reading that map leaves fetch unauthenticated and the API returns 401.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = await b2cAuthService.getValidToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    // Surface 402 / 429 / 5xx as exceptions so the modal's fallback path can
    // decide whether to retry with the non-streaming endpoint.
    throw Object.assign(new Error(`SSE request failed (${response.status})`), {
      status: response.status,
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) return;

    buffer += decoder.decode(value, { stream: true });

    // SSE messages are separated by blank lines; each message can have
    // multiple `data:` lines but our backend always sends one per message.
    let separatorIndex = buffer.indexOf('\n\n');
    while (separatorIndex !== -1) {
      const rawMessage = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const dataLine = rawMessage
        .split('\n')
        .find((line) => line.startsWith('data:'));
      if (dataLine) {
        const payload = dataLine.slice(5).trim();
        if (payload.length > 0) {
          try {
            onEvent(JSON.parse(payload) as GreetingStreamEvent);
          } catch {
            // Malformed event — surface as a generic error so the caller can
            // fall back to the non-streaming endpoint.
            onEvent({ event: 'error', message: 'Malformed stream event', code: 'PARSE_ERROR' });
          }
        }
      }

      separatorIndex = buffer.indexOf('\n\n');
    }
  }
};

/**
 * Updates the per-event AI settings (instructions, Tone override, Notes gate)
 * for a contact + event combination.
 */
export const updateAiSettings = async (
  contactId: number,
  eventTypeId: number,
  payload: {
    aiGuidance?: string | null;
    preferredTone?: string | null;
    includeNotesInAiPrompt?: boolean;
  }
): Promise<void> => {
  await apiClient.patch(
    `/greeting-generation/contacts/${contactId}/event-types/${eventTypeId}/ai-guidance`,
    payload
  );
};

/**
 * Copies the contact-level {@code AiInstructions} into the per-event
 * {@code AiGuidance} on demand. Backs the "Reset to default" affordance in the
 * Edit AI settings modal. Never runs automatically on event-type creation —
 * that would silently overwrite per-event edits.
 */
export const resetAiGuidanceToDefault = async (
  contactId: number,
  eventTypeId: number
): Promise<void> => {
  await apiClient.post(
    `/greeting-generation/contacts/${contactId}/event-types/${eventTypeId}/reset-ai-guidance`
  );
};
